import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import {
  renderFollowerGoalDesignSvg,
  type FollowerGoalDesign,
} from "../../../../data/followerGoalDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/** Counts pushed in by the parent renderer each poll tick. */
export interface FollowerGoalCounts {
  current: number;
  target: number;
}

/**
 * Design-store render strategy for the FollowerGoal widget — the count-driven
 * sibling of `ProgressBarDesignRenderer`. Renders the chosen design's CARD SVG
 * (from the local catalog) as a single Pixi sprite stretched to the widget
 * bounds, re-rasterising the texture only when the displayed `current/target`
 * pair actually changes.
 *
 * Unlike ProgressBar/Countdown design renderers it does NOT compute its own
 * value or subscribe to a tick: the parent `FollowerGoalWidgetRenderer` already
 * owns the once-per-second poll of `OverlayEventStatsRepository` (auto) / the
 * static `manualCurrent` (manual), so it just calls {@link update} with the
 * resolved counts. One instance per widget (kept in the parent's Map).
 */
export class FollowerGoalDesignRenderer {
  private container: Container | null = null;

  private sprite: Sprite | null = null;

  /** Widget bounds the sprite is stretched to — re-asserted after each swap. */
  private destWidth = 1;

  private destHeight = 1;

  /** Backing-store multiplier for the rasterised card bitmap. */
  private renderScale = 1;

  /** Last `current/target` key baked into the current texture. */
  private lastKey: string | null = null;

  /** Most recent counts pushed in (re-drawn once fonts finish decoding). */
  private lastCounts: FollowerGoalCounts | null = null;

  /** Texture currently shown — destroyed before being replaced. */
  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  constructor(private readonly design: FollowerGoalDesign) {}

  render(
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    this.container = new Container();
    parentContainer.addChild(this.container);

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    // Backing-store resolution: rasterise at the larger of the card's native
    // size and the on-canvas size, capped so an oversized widget can't ask for
    // a multi-thousand-px bitmap. The sprite is then scaled to the bounds.
    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Preload the design's Google fonts, then force a redraw with the latest
    // counts so the real typeface (not a system fallback) is baked in.
    void ensureDesignFontsReady(this.design.fonts).then(() => {
      if (this.destroyed) return;
      this.lastKey = null;
      if (this.lastCounts) void this.rerasterize(this.lastCounts);
    });
  }

  /** Push fresh counts; re-rasterises only when `current/target` changes. */
  update(counts: FollowerGoalCounts): void {
    if (this.destroyed || !this.sprite) return;
    this.lastCounts = counts;
    const key = `${counts.current}/${counts.target}`;
    if (key === this.lastKey) return;
    void this.rerasterize(counts);
  }

  /**
   * (Re)build the card SVG → texture for the given counts and swap it onto the
   * sprite. Generation-guarded so a slow decode that resolves after a newer one
   * can't clobber the fresher frame, and after teardown.
   */
  private async rerasterize(counts: FollowerGoalCounts): Promise<void> {
    if (this.destroyed || !this.sprite) return;

    // Claim this generation/key immediately so the equality guard in update()
    // doesn't fire a second raster for the same counts mid-decode.
    this.lastKey = `${counts.current}/${counts.target}`;
    const gen = ++this.generation;

    const svg = renderFollowerGoalDesignSvg(this.design, counts);

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      // A newer raster (or teardown) won the race — drop this one.
      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      // Assigning a texture in Pixi v8 resets the sprite to the texture's
      // intrinsic (backing-store) size — re-assert the widget bounds so the
      // card always fills `destination` regardless of renderScale.
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      // Decode failed (malformed SVG / browser quirk) — keep the previous frame
      // on screen rather than blanking. Allow a future update to retry.
      if (!this.destroyed && gen === this.generation) {
        this.lastKey = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;
    this.lastCounts = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
